/**
 * components/panel.tsx
 * The design's card container: bordered card surface with an optional header
 * row (title + right-aligned action slot) and a body. Shared across screens.
 */
import React from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../context/themeContext';
import { web } from '../utils/web';

export interface PanelProps {
  title?: string;
  action?: React.ReactNode;
  /** Remove body padding (e.g. for full-bleed table rows). */
  flush?: boolean;
  children: React.ReactNode;
  style?: object;
}

export default function Panel({ title, action, flush, children, style }: PanelProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  return (
    <View
      style={[
        { backgroundColor: colors.card, borderColor: colors.line, borderWidth: 1, borderRadius: radii.lg },
        web({ boxShadow: '0 1px 0 rgba(16,24,40,0.04)' }),
        style,
      ]}
    >
      {(title != null || action != null) && (
        // Header wraps: when the action slot is too wide to share the row (narrow
        // viewports), it drops to its own line instead of squeezing the title
        // into a char-by-char wrap. Title shrinks + truncates before that.
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: 12, rowGap: 10, paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: colors.line }}>
          {title != null ? <Text numberOfLines={1} style={{ flexShrink: 1, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{title}</Text> : <View />}
          {action}
        </View>
      )}
      <View style={flush ? undefined : { padding: 16 }}>{children}</View>
    </View>
  );
}
